import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography, CircularProgress, Alert, Grid } from '@mui/material';
import * as svc from '../services/analyticsService';
import {
  AnalyticsFilters, BreakdownDimension, Granularity, ViewKey, ALL_VIEW, MIN_DATE,
} from '../services/analyticsService';
import FilterPanel from '../components/analytics/FilterPanel';
import {
  KPICards, VolumeChart, WeekdayChart, DurationChart, BreakdownChart, HeatmapGrid,
  periodoText, resolvePeriod, autoGranularity, DESC, TITLES, CHART_KEYS,
  type ChartKey, type TextEdit,
} from '../components/analytics/AnalyticsCharts';
import {
  expandIntentoFilter, canonIntentoOptions, expandCategoriaFilter, BREVI_LABEL,
} from '../components/analytics/intentoTaxonomy';
import { useAuth } from '../contexts/AuthContext';
import { useComuneConfig } from '../contexts/ConfigContext';

const EMPTY: AnalyticsFilters = { period: 'sempre' };
// Default state of the page, restored both on load and by Azzera: NO dates. No
// window is assumed for anyone — the charts stay hidden until a period or the
// da/a dates are chosen, so what is on screen is always a window someone picked.
const DEFAULT_FILTERS: AnalyticsFilters = {};
const NO_DATES: AnalyticsFilters = { period: undefined, from: undefined, to: undefined };
// A window is set by a period, or by BOTH da and a: one date alone is half a
// choice, and explicit dates override the period, so they decide when present.
const hasDates = (f: AnalyticsFilters) => (f.from || f.to ? !!(f.from && f.to) : !!f.period);
// "Non classificato" stays in the option list on purpose: it is a named, drillable
// bucket now, so every bar on the chart can be filtered on. The brief bucket does
// not: it is no office or service, and filtering on it as one returned zero.
const optVals = (items: svc.BreakdownItem[]) =>
  items.map(x => x.value).filter(v => v !== BREVI_LABEL);

// Built-in default view presets (frontend-only; no filters, view-focused).
const BUILTIN_PRESETS: svc.FilterPreset[] = [
  { id: 'view:sommario', name: 'Riepilogo chiamate', builtin: true, content: { filters: {}, view: ['kpi'], description: 'Totale chiamate, gestite in autonomia o trasferite, prenotazioni e costi' } },
  { id: 'view:volume', name: 'Volume chiamate', builtin: true, content: { filters: {}, view: ['volume'], description: 'Andamento delle chiamate ricevute per giorno, settimana o mese' } },
  // "Motivo di contatto" = only L1, no MIN_DATE limit.
  { id: 'view:motivo', name: 'Chiamate per motivo di contatto', builtin: true, content: { filters: {}, view: ['motivo'], description: 'Chiamate suddivise per il principale motivo per cui il cittadino chiama' } },
  // Full L1/L2/L3 — this is where the MIN_DATE limit lives (via 'categorie').
  { id: 'view:categorie', name: 'Chiamate ufficio/categoria & dettaglio servizi', builtin: true, content: { filters: {}, view: ['categorie'], description: 'Chiamate suddivise per ufficio competente e per servizio richiesto (disponibile dal 01/08/2026)' } },
  { id: 'view:distribuzioni', name: 'Chiamate per periodo', builtin: true, content: { filters: {}, view: ['distribuzioni'], description: 'Distribuzione delle chiamate per giorno della settimana e fascia oraria' } },
  { id: 'view:brevi', name: 'Chiamate brevi non trasferite', builtin: true, content: { filters: { brevi: '1' }, view: ['volume'], description: 'Andamento delle chiamate sotto i 25 secondi non trasferite, per giorno, settimana o mese' } },
];

const AnalyticsPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const { liveSince, displayName: comune } = useComuneConfig();
  const [filters, setFilters] = useState<AnalyticsFilters>(DEFAULT_FILTERS);
  const [options, setOptions] = useState<Record<BreakdownDimension, string[]>>({
    intento: [], macro_categoria: [], servizio: [],
  });
  // A granularity picked on the chart, remembered for the window it was picked
  // in only: a new window goes back to the automatic one.
  const [granularityChoice, setGranularityChoice] =
    useState<{ window: string; g: Granularity } | null>(null);
  // empty view = "no preset applied yet": nothing is shown.
  const [view, setView] = useState<ViewKey[]>([]);
  const [activePresetId, setActivePresetId] = useState<string | number | ''>('');
  // Chart title/description overrides. `texts` is what the page shows right now,
  // edits included; `savedTexts` is what the active report carries. They differ
  // exactly when there are edits a save would keep and a report change would lose.
  const [texts, setTexts] = useState<svc.ChartTexts>({});
  const [savedTexts, setSavedTexts] = useState<svc.ChartTexts>({});

  const [kpi, setKpi] = useState<svc.KpiSummary | null>(null);
  const [volume, setVolume] = useState<svc.VolumePoint[]>([]);
  const [weekday, setWeekday] = useState<svc.WeekdayPoint[]>([]);
  const [heatmap, setHeatmap] = useState<svc.HeatmapCell[]>([]);
  const [duration, setDuration] = useState<svc.DurationBucket[]>([]);
  const [bdIntento, setBdIntento] = useState<svc.BreakdownItem[]>([]);
  const [bdMacro, setBdMacro] = useState<svc.BreakdownItem[]>([]);
  const [bdServizio, setBdServizio] = useState<svc.BreakdownItem[]>([]);

  const [presets, setPresets] = useState<svc.FilterPreset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportingReport, setExportingReport] = useState(false);

  const filtersKey = JSON.stringify(filters);

  const loadPresets = useCallback(() => {
    svc.getPresets().then(setPresets).catch(() => { /* best-effort */ });
  }, []);
  useEffect(() => { loadPresets(); }, [loadPresets]);

  const allPresets = [...BUILTIN_PRESETS, ...presets];
  const activePreset = allPresets.find(p => String(p.id) === String(activePresetId));

  // Volume granularity, first match wins: picked on the chart for this window,
  // pinned by the report, else derived from the window's length.
  const windowKey = `${filters.period}|${filters.from}|${filters.to}`;
  const granularity: Granularity =
    (granularityChoice?.window === windowKey ? granularityChoice.g : undefined)
    ?? activePreset?.content.granularity
    ?? autoGranularity(filters);
  const setGranularity = (g: Granularity) => setGranularityChoice({ window: windowKey, g });

  // The earliest day a report may show, or undefined for no floor. Two floors,
  // read from one place so the calendar and the reset cannot disagree:
  //   * the day the comune went into service — before it there are only test
  //     calls (also enforced server-side, this is the courtesy);
  //   * for a USER on the L2/L3 report, the day the taxonomy started being
  //     filled (MIN_DATE).
  // ISO strings sort as dates, so the last one is the later.
  const minDateFor = (v: ViewKey[]) => [
    liveSince ?? undefined,
    !isAdmin && v.includes('categorie') ? MIN_DATE : undefined,
  ].filter(Boolean).sort().pop();

  // Changing report sets the dates the report was saved with, or none when it has
  // none: dates chosen for one report are never carried into the next, where
  // they were never chosen.
  //
  // The minimum dates still hold. The floor is compared with where the window
  // ACTUALLY starts — the first of the month, for "questo mese" — and only
  // replaces the period with an explicit range when that start falls below it.
  // Comparing against the bare `from`, which is empty whenever a period is
  // selected, used to fire every time and, since an explicit date beats the
  // period, silently turned "this month" into "since 30 July".
  const applyPreset = (preset: svc.FilterPreset) => {
    const nextView = preset.content.view.length ? preset.content.view : ALL_VIEW;
    const { period, from, to } = preset.content.filters;
    let time: AnalyticsFilters = { ...NO_DATES, period, from, to };
    // There is no Periodo field: a period saved by an older report ("questo
    // mese") is shown as the dates it stands for, so Da/A never sit empty over
    // a window.
    if (period && !from && !to) {
      const r = resolvePeriod(period);
      if (r.from || r.to) time = { ...NO_DATES, ...r };
    }
    // Explicit dates win over the period, as in the request itself.
    const effective = time.from || time.to ? time : resolvePeriod(time.period);
    const floor = minDateFor(nextView);
    if (hasDates(time) && floor && (!effective.from || effective.from < floor)) {
      // "Sempre" has no end: close it at today, so the window has both dates.
      time = { period: EMPTY.period, from: floor, to: effective.to ?? resolvePeriod('oggi').to };
    }
    setFilters({ ...EMPTY, ...preset.content.filters, ...time });
    setView(nextView);
    // A granularity picked on the previous report does not follow.
    setGranularityChoice(null);
    setActivePresetId(preset.id);
    // Each report brings its own texts, or none: edits made on the previous one
    // do not follow, like its dates.
    setTexts(preset.content.texts ?? {});
    setSavedTexts(preset.content.texts ?? {});
  };
  // Azzera returns the page to exactly what a fresh load shows.
  const resetAll = () => {
    setFilters(DEFAULT_FILTERS);
    setView([]);
    setActivePresetId('');
    setGranularityChoice(null);
    setTexts({});
    setSavedTexts({});
  };
  // Admin save: capture the current filters, dates included, + the sections chosen
  // in the dialog. A relative period ("questo mese") stays relative when reopened.
  const savePreset = (
    name: string, presetView: ViewKey[], presetGranularity?: Granularity, description?: string,
    lockDates?: boolean,
  ) => {
    // Only the overrides travel — a chart left on its default text is stored as
    // nothing, so if the default wording changes later, it changes there too.
    const saved = { ...texts };
    svc.savePreset(name, {
      filters, view: presetView, granularity: presetGranularity,
      texts: Object.keys(saved).length ? saved : undefined,
      description: description || undefined,
      lockDates: (lockDates && hasDates(filters)) || undefined,
    })
      .then(() => { setSavedTexts(saved); loadPresets(); })
      .catch(() => setError('Errore nel salvataggio del report.'));
  };
  // Changes only the description: the report keeps its own filters, dates and
  // texts, not the ones currently on the page. Saving upserts by name.
  const editDescription = (preset: svc.FilterPreset, description: string) => {
    svc.savePreset(preset.name, { ...preset.content, description: description || undefined })
      .then(loadPresets)
      .catch(() => setError('Errore nel salvataggio della descrizione.'));
  };
  const removePreset = (id: number) => {
    svc.deletePreset(id).then(loadPresets).catch(() => setError('Errore nell\'eliminazione del report.'));
  };

  // Only the active report's sections, for admins and users alike: nothing is
  // shown until a report is picked — first the report, then the dates.
  const show = (k: ViewKey) => view.includes(k);

  // The UI works in canonical intento labels; the backend stores raw values, so
  // expand a canonical intento pick (named → raw values, "Altro" → catch-all
  // token) before every fetch.
  const expand = useCallback((f: AnalyticsFilters): AnalyticsFilters => {
    const out = { ...f };
    if (f.intento?.length) out.intento = expandIntentoFilter(f.intento);
    if (f.macro_categoria?.length) out.macro_categoria = expandCategoriaFilter(f.macro_categoria);
    if (f.servizio?.length) out.servizio = expandCategoriaFilter(f.servizio);
    return out;
  }, []);

  // L1 options: all intento values (canonical), fetched once.
  useEffect(() => {
    svc.getBreakdown({}, 'intento')
      .then(items => setOptions(o => ({ ...o, intento: canonIntentoOptions(optVals(items)) })))
      .catch(() => { /* best-effort */ });
  }, []);

  // L2 options depend on the selected L1 (data-driven cascade): only the macro
  // categories that co-occur with the chosen L1 in the data. Prune L2 picks that
  // are no longer valid under the new L1.
  useEffect(() => {
    svc.getBreakdown(expand({ intento: filters.intento }), 'macro_categoria')
      .then(items => {
        const opts = optVals(items);
        setOptions(o => ({ ...o, macro_categoria: opts }));
        setFilters(prev => {
          const kept = (prev.macro_categoria ?? []).filter(v => opts.includes(v));
          return kept.length === (prev.macro_categoria?.length ?? 0) ? prev : { ...prev, macro_categoria: kept };
        });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters.intento)]);

  // L3 options depend on L1 + L2; prune L3 picks no longer valid.
  useEffect(() => {
    svc.getBreakdown(expand({ intento: filters.intento, macro_categoria: filters.macro_categoria }), 'servizio')
      .then(items => {
        const opts = optVals(items);
        setOptions(o => ({ ...o, servizio: opts }));
        setFilters(prev => {
          const kept = (prev.servizio ?? []).filter(v => opts.includes(v));
          return kept.length === (prev.servizio?.length ?? 0) ? prev : { ...prev, servizio: kept };
        });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters.intento), JSON.stringify(filters.macro_categoria)]);

  const datesSet = hasDates(filters);
  // Charts need both choices: a report (what to show) and a window (over when).
  const ready = activePresetId !== '' && datesSet;

  // Full reload whenever the global filters change — and only once there is a
  // report and a window to report on.
  useEffect(() => {
    if (!ready) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    setError(null);
    const eff = expand(filters);
    Promise.all([
      svc.getKpi(eff),
      svc.getVolume(eff, granularity),
      svc.getWeekday(eff),
      svc.getHeatmap(eff),
      svc.getDuration(eff),
      svc.getBreakdown(eff, 'intento'),
      svc.getBreakdown(eff, 'macro_categoria'),
      svc.getBreakdown(eff, 'servizio'),
    ])
      .then(([k, v, w, h, d, bi, bm, bs]) => {
        if (!alive) return;
        setKpi(k); setVolume(v); setWeekday(w); setHeatmap(h); setDuration(d);
        setBdIntento(bi); setBdMacro(bm); setBdServizio(bs);
      })
      .catch(e => { if (alive) setError(e?.message ?? 'Errore nel caricamento delle metriche.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey, ready]);

  // Granularity toggle → refetch only the volume chart.
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    if (!ready) return;
    svc.getVolume(expand(filters), granularity).then(setVolume).catch(() => { /* keep prior */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [granularity]);

  const pickerMinDate = minDateFor(view);

  // Passed into every chart so the selected window lands inside the captured PNG.
  const periodo = { period: filters.period, from: filters.from, to: filters.to };

  // The sections actually rendered.
  const shownView = svc.ALL_VIEW.filter(show);
  const activeReportName = activePreset?.name ?? 'Report chiamate';
  // Fixed by the report: read-only for users, still editable by admins.
  const datesLocked = !isAdmin && !!activePreset?.content.lockDates;

  // Default texts per chart. The volume chart has two: it speaks of "chiamate
  // brevi" when the brevi report is active.
  const isBrevi = activePresetId === 'view:brevi';
  const defaults: Record<ChartKey, { title: string; description: string }> = {
    kpi: { title: TITLES.kpi, description: DESC.kpi },
    volume: isBrevi
      ? { title: TITLES.brevi, description: DESC.brevi }
      : { title: TITLES.volume, description: DESC.volume },
    motivo: { title: TITLES.motivo, description: DESC.motivo },
    ufficio: { title: TITLES.ufficio, description: DESC.ufficio },
    servizio: { title: TITLES.servizio, description: DESC.servizio },
    weekday: { title: TITLES.weekday, description: DESC.weekday },
    durata: { title: TITLES.durata, description: DESC.durata },
    heatmap: { title: TITLES.heatmap, description: DESC.heatmap },
  };
  // What a chart shows: the report's override where it has one, else the default.
  // One function for card, PNG and export, so the three cannot disagree.
  const textOf = (k: ChartKey) => ({
    title: texts[k]?.title ?? defaults[k].title,
    description: texts[k]?.description ?? defaults[k].description,
  });
  // Admins only. An override records just the fields that differ from the
  // default, and an entry that differs in nothing is dropped.
  const editOf = (k: ChartKey): TextEdit | undefined => (!isAdmin ? undefined : {
    customised: !!texts[k],
    onApply: (title, description) => setTexts(t => {
      const entry: svc.ChartText = {};
      if (title !== defaults[k].title) entry.title = title;
      if (description !== defaults[k].description) entry.description = description;
      const next = { ...t };
      if (Object.keys(entry).length) next[k] = entry; else delete next[k];
      return next;
    }),
    onReset: () => setTexts(t => { const next = { ...t }; delete next[k]; return next; }),
  });
  const chartProps = (k: ChartKey) => ({ ...textOf(k), edit: editOf(k) });
  // Compared per chart and per field, not as serialized JSON, which would call
  // two equal objects different the moment their keys came in another order.
  const textsUnsaved = CHART_KEYS.some(k =>
    (texts[k]?.title ?? '') !== (savedTexts[k]?.title ?? '')
    || (texts[k]?.description ?? '') !== (savedTexts[k]?.description ?? ''));
  // Keys match the sheets the server writes; the file echoes these verbatim.
  const exportTexts = Object.fromEntries(CHART_KEYS.map(k => [k, textOf(k)]));

  const handleReportExport = async () => {
    setExportingReport(true);
    try {
      await svc.downloadReportExport(
        expand(filters), shownView, granularity, activeReportName, periodoText(periodo),
        exportTexts,
      );
    } catch { setError('Errore durante l\'esportazione del report.'); }
    finally { setExportingReport(false); }
  };

  const handleExport = async () => {
    setExporting(true);
    try { await svc.downloadExport(expand(filters)); }
    catch { setError('Errore durante l\'esportazione.'); }
    finally { setExporting(false); }
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>Analisi Chiamate</Typography>

      {/* One banner, for admins and users, while there is nothing to show yet. */}
      {!ready && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Selezionare prima il report, poi le date Da e A.
        </Alert>
      )}

      <FilterPanel
        value={filters}
        options={options}
        onChange={setFilters}
        onExport={handleExport}
        onReset={resetAll}
        exporting={exporting}
        onReportExport={handleReportExport}
        exportingReport={exportingReport}
        canExportReport={ready && shownView.length > 0}
        presets={allPresets}
        presetId={activePresetId}
        minDate={pickerMinDate}
        isAdmin={isAdmin}
        onApplyPreset={applyPreset}
        onSavePreset={savePreset}
        onEditDescription={editDescription}
        datesLocked={datesLocked}
        textsUnsaved={isAdmin && textsUnsaved}
        onDeletePreset={removePreset}
        granularity={granularity}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!ready ? null : loading ? (
        <Box display="flex" justifyContent="center" mt={5}><CircularProgress /></Box>
      ) : kpi && (
        <>
          {show('kpi') && (
            <KPICards kpi={kpi} periodo={periodo} comune={comune} {...chartProps('kpi')} />
          )}
          <Grid container spacing={3} sx={{ mt: 0 }}>
            {show('volume') && (
              <Grid item xs={12}>
                <VolumeChart
                  data={volume} granularity={granularity} onGranularityChange={setGranularity}
                  periodo={periodo} comune={comune} {...chartProps('volume')}
                />
              </Grid>
            )}
            {show('motivo') && (
              <Grid item xs={12}>
                {/* Drawn as received: the backend folds to canonical labels and
                    lifts the too-brief calls into their own bucket. */}
                <BreakdownChart data={bdIntento} periodo={periodo} comune={comune}
                  {...chartProps('motivo')} />
              </Grid>
            )}
            {show('categorie') && (
              <>
                <Grid item xs={12}>
                  <BreakdownChart data={bdMacro} maxBars={bdMacro.length} periodo={periodo}
                    comune={comune} {...chartProps('ufficio')} />
                </Grid>
                <Grid item xs={12}>
                  <BreakdownChart data={bdServizio} periodo={periodo}
                    comune={comune} {...chartProps('servizio')} />
                </Grid>
              </>
            )}
            {show('distribuzioni') && (
              <>
                <Grid item xs={12} md={6}>
                  <WeekdayChart data={weekday} periodo={periodo} comune={comune} {...chartProps('weekday')} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DurationChart data={duration} periodo={periodo} comune={comune} {...chartProps('durata')} />
                </Grid>
                <Grid item xs={12}>
                  <HeatmapGrid data={heatmap} periodo={periodo} comune={comune} {...chartProps('heatmap')} />
                </Grid>
              </>
            )}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default AnalyticsPage;
